import {
  PolymorphicComponentPropWithRef,
  PolymorphicRef,
} from "@kunduz/utility-polymorphic-component";
import React from "react";
import classNames from "classnames";
import styles from "./text.module.scss";

type TextVariant =
  | "textxs"
  | "textsm"
  | "textmd"
  | "textlg"
  | "textxl"
  | "displayxs"
  | "displaysm"
  | "displaymd"
  | "displaylg"
  | "displayxl"
  | "display2xl";

type TextWeight = "regular" | "medium" | "semibold" | "bold";

type GradientVariant =
  | "blue"
  | "gray"
  | "red"
  | "green"
  | "tutorGreen"
  | "purple";

type EllipsisVariant = number;

type TextProps<C extends React.ElementType> = PolymorphicComponentPropWithRef<
  C,
  {
    variant?: TextVariant | "displaymd";
    weight?: TextWeight | "regular";
    gradient?: GradientVariant;
    ellipsis?: EllipsisVariant;
  }
>;

type TextComponent = <C extends React.ElementType = "p">(
  props: TextProps<C>,
) => React.ReactElement | null | any;

export const Text: TextComponent = React.forwardRef(
  <C extends React.ElementType = "p">(
    {
      as,
      children,
      variant,
      weight,
      gradient,
      className,
      ellipsis,
      style,
      ...rest
    }: TextProps<C>,
    ref?: PolymorphicRef<C>,
  ) => {
    const Component = as || "p";
    return (
      <Component
        ref={ref}
        {...rest}
        className={classNames(
          styles.base,
          styles[`weight-${weight}`],
          styles[`variant-${variant}`],
          styles[`color-${gradient}`],
          {
            [styles["ellipsis"]]: !!ellipsis,
          },
          className,
        )}
        style={{
          ...style,
          ...(ellipsis && { WebkitLineClamp: ellipsis }),
        }}
      >
        {children}
      </Component>
    );
  },
);

//@ts-ignore
Text.displayName = "Text";
